import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFrontendAuth } from '../hooks/useAuth';
import {
    getMyChildren,
    deleteChild,
    getRecommendationsForChild,
    updateChecklistItem,
    addCustomChecklistItem,
    deleteChecklistItem,
    getChecklistPhases,
    getGrowthInsight,
} from '../services/api.shop';
import {
    ChildDto,
    Product,
    ChecklistItemWithPhase,
    ChecklistPhase,
    GrowthInsightDto,
    Gender,
} from '../types/index';
import ChecklistItemRow from '../components/ChecklistItemRow';
import ChildModal from '../components/ChildModal';
import ProductCard from '../components/ProductCard';
import ProductResultsToolbar from '../components/ProductResultsToolbar';
import { Dialog } from '@headlessui/react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { Baby, Edit2, Plus, Search } from 'lucide-react';
import BackToHomeLink from '../components/BackToHomeLink';
import ConfirmModal from '../components/ConfirmModal';
import GrowthRoadmap from '../components/myrebaby/GrowthRoadmap';
import PhaseEducationalCards from '../components/myrebaby/PhaseEducationalCards';
import {
    DEFAULT_PRODUCT_SORT,
    ProductSortKey,
    sortProductsClient,
} from '../utils/productSort';
import { scrollToElement } from '../utils/scrollToElement';

/** Preview length so long phases stay scannable without infinite scroll / missing items. */
const CHECKLIST_PREVIEW_COUNT = 8;

/** Phase tint from CMS only; otherwise site settings accent (no client palette). */
function resolvePhaseAccent(phase: ChecklistPhase | undefined): string {
    const fromApi = phase?.content?.accentColor?.trim();
    return fromApi || 'var(--accent-color)';
}

const getChildAvatarEmoji = (child: ChildDto): string => {
    if (child.expectedBirthDate) return '🤰';
    switch (child.gender) {
        case Gender.Boy: return '👦';
        case Gender.Girl: return '👧';
        default: return '👶';
    }
};

/** Roadmap chips use 9–12m; insight copy must too, with the nickname as a hint. */
function formatInsightPhaseLabel(
    phase: ChecklistPhase | undefined,
    t: (key: string) => string,
    fallbackName: string
): string {
    if (!phase) return fallbackName;
    if (phase.minAgeMonths < 0) return t('page.myrebaby.phase.pregnancy');
    const age = `${phase.minAgeMonths}–${phase.maxAgeMonths}m`;
    const name = phase.content?.displayNameKey ? t(phase.content.displayNameKey) : '';
    return name ? `${age} · ${name}` : age;
}

const getChildAgeLabel = (child: ChildDto): string => {
    const now = new Date();
    if (child.birthDate) {
        const birth = new Date(child.birthDate);
        const months = Math.floor((now.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24 * 30));
        return `${months} mo`;
    }
    if (child.expectedBirthDate) {
        const expected = new Date(child.expectedBirthDate);
        const monthsTo = Math.max(0, Math.floor((expected.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30)));
        return `+${monthsTo} mo`;
    }
    return '—';
};

const myRebabySelectionKey = (userId: string) => `myrebaby:selection:${userId}`;

type MyRebabySelection = { childId: number; phaseId: number };

function readMyRebabySelection(userId: string | undefined): MyRebabySelection | null {
    if (!userId || typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(myRebabySelectionKey(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as MyRebabySelection;
        if (typeof parsed?.childId === 'number' && typeof parsed?.phaseId === 'number') return parsed;
    } catch {
        /* ignore */
    }
    return null;
}

function writeMyRebabySelection(userId: string | undefined, childId: number | null, phaseId: number) {
    if (!userId || typeof sessionStorage === 'undefined') return;
    if (childId == null || childId <= 0) {
        sessionStorage.removeItem(myRebabySelectionKey(userId));
        return;
    }
    try {
        sessionStorage.setItem(
            myRebabySelectionKey(userId),
            JSON.stringify({ childId, phaseId: phaseId > 0 ? phaseId : 0 } satisfies MyRebabySelection)
        );
    } catch {
        /* ignore quota */
    }
}

const MyRebaby: React.FC = () => {
    const { t } = useTranslation();
    // No English placeholders — missing translations surface as the key so gaps are obvious
    const tr = (key: string) => t(key);
    const { isAuthReady, user } = useFrontendAuth();
    const queryClient = useQueryClient();
    const userId = user?.id;
    const productsRef = useRef<HTMLDivElement>(null);

    const [children, setChildren] = useState<ChildDto[]>([]);
    const [phases, setPhases] = useState<ChecklistPhase[]>([]);
    const [selectedChild, setSelectedChild] = useState<ChildDto | null>(null);
    const [modalInitialExpected, setModalInitialExpected] = useState<boolean | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPhaseId, setSelectedPhaseId] = useState<number>(0);
    const [selectedRecChildId, setSelectedRecChildId] = useState<number | null>(null);
    const [recommendations, setRecommendations] = useState<Record<number, Product[]>>({});
    const [recLoading, setRecLoading] = useState<Record<number, boolean>>({});
    const [checklistLoading, setChecklistLoading] = useState<Record<number, boolean>>({});
    const [customItemName, setCustomItemName] = useState<string>('');
    const [addingCustom, setAddingCustom] = useState<boolean>(false);
    const [showCustomAdd, setShowCustomAdd] = useState(false);
    const [growthInsight, setGrowthInsight] = useState<GrowthInsightDto | null>(null);
    const [isInsightLoading, setIsInsightLoading] = useState(false);
    const [recSortKey, setRecSortKey] = useState<ProductSortKey>(DEFAULT_PRODUCT_SORT);
    const [confirmDeleteCustomId, setConfirmDeleteCustomId] = useState<number | null>(null);
    const [confirmDeleteChildId, setConfirmDeleteChildId] = useState<number | null>(null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const [checklistExpanded, setChecklistExpanded] = useState(false);
    const selectedRecChildIdRef = useRef<number | null>(null);

    const { data: childrenData, isLoading: childrenLoading } = useProtectedQuery({
        queryKey: ['myChildren'],
        queryFn: () => getMyChildren().then(res => res.data),
        staleTime: 1000 * 60 * 5,
    });

    useEffect(() => {
        if (childrenData) setChildren(childrenData);
    }, [childrenData]);

    useEffect(() => {
        getChecklistPhases()
            .then(data => setPhases(Array.isArray(data) ? data : []))
            .catch(err => {
                console.error('Failed to load checklist phases:', err);
                setPhases([]);
            });
    }, []);

    useEffect(() => {
        if (children.length === 0 || phases.length === 0 || selectedRecChildId !== null) return;

        const stored = readMyRebabySelection(userId);
        const restoredChild =
            stored != null ? children.find(c => c.id === stored.childId) : undefined;

        if (restoredChild && stored) {
            setSelectedRecChildId(restoredChild.id);
            const phaseExists = phases.some(p => p.id === stored.phaseId);
            if (phaseExists && stored.phaseId > 0) {
                setSelectedPhaseId(stored.phaseId);
                void fetchRecommendations(restoredChild.id, stored.phaseId);
            } else {
                autoSelectPhaseForChild(restoredChild);
            }
            return;
        }

        const firstChild = children[0];
        setSelectedRecChildId(firstChild.id);
        autoSelectPhaseForChild(firstChild);
    }, [children, phases, selectedRecChildId, userId]);

    useEffect(() => {
        selectedRecChildIdRef.current = selectedRecChildId;
    }, [selectedRecChildId]);

    useEffect(() => {
        if (selectedRecChildId != null) {
            writeMyRebabySelection(userId, selectedRecChildId, selectedPhaseId);
        }
    }, [userId, selectedRecChildId, selectedPhaseId]);

    const fetchRecommendations = async (childId: number, phaseId: number, forceRefresh = false) => {
        if (phaseId <= 0) return;

        setRecLoading(prev => ({ ...prev, [childId]: true }));
        if (forceRefresh) {
            setRecommendations(prev => ({ ...prev, [childId]: [] }));
            if (selectedRecChildIdRef.current === childId) {
                setGrowthInsight(null);
            }
        }

        setIsInsightLoading(true);
        try {
            const [products, insight] = await Promise.all([
                getRecommendationsForChild(childId, phaseId),
                getGrowthInsight(childId, phaseId),
            ]);
            setRecommendations(prev => ({ ...prev, [childId]: products || [] }));
            if (selectedRecChildIdRef.current === childId) {
                setGrowthInsight(insight);
            }
        } catch (err) {
            console.error('Error fetching recommendations/growth insight:', err);
            if (selectedRecChildIdRef.current === childId) {
                setGrowthInsight(null);
            }
            setRecommendations(prev => ({ ...prev, [childId]: [] }));
        } finally {
            setIsInsightLoading(false);
            setRecLoading(prev => ({ ...prev, [childId]: false }));
        }
    };

    const autoSelectPhaseForChild = (child: ChildDto, forceRefresh = true) => {
        if (!phases.length) {
            // Phases not ready yet — select child; effect below will run phase + recs when phases load
            setSelectedRecChildId(child.id);
            setSelectedPhaseId(0);
            return;
        }

        // Expecting (not yet born): prenatal phase only. Born: match postnatal age bands (never Expecting).
        let currentPhase: ChecklistPhase | undefined;
        if (child.expectedBirthDate && !child.birthDate) {
            currentPhase = phases.find((p) => p.minAgeMonths < 0) || phases[0];
        } else {
            const calendarMonths = child.birthDate
                ? Math.floor(
                      (Date.now() - new Date(child.birthDate).getTime()) /
                          (1000 * 60 * 60 * 24 * 30.4375)
                  )
                : 0;
            const fitMonths =
                child.suggestedFitAgeMonths != null && !child.measurementsStale
                    ? Math.round(child.suggestedFitAgeMonths)
                    : calendarMonths;
            const postnatal = phases.filter((p) => p.minAgeMonths >= 0);
            currentPhase =
                postnatal.find(
                    (p) => fitMonths >= p.minAgeMonths && fitMonths <= p.maxAgeMonths
                ) || postnatal[0] || phases[0];
        }

        setSelectedPhaseId(currentPhase.id);
        writeMyRebabySelection(userId, child.id, currentPhase.id);
        // forceRefresh so growth insight + product strip show loading and refresh immediately (T222)
        void fetchRecommendations(child.id, currentPhase.id, forceRefresh);
    };

    // After new child when phases were empty: run phase pick + recs as soon as phases arrive
    useEffect(() => {
        if (!phases.length || selectedRecChildId == null || selectedPhaseId > 0) return;
        const child = children.find(c => c.id === selectedRecChildId);
        if (child) autoSelectPhaseForChild(child, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only when phases/child selection needs phase
    }, [phases, selectedRecChildId, selectedPhaseId, children]);

    const updateChecklistMutation = useMutation({
        mutationFn: ({ childId, itemId, isChecked }: { childId: number; itemId: number; isChecked: boolean }) =>
            updateChecklistItem(childId, itemId, { isChecked }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['myChildren'] }),
    });

    const addCustomMutation = useMutation({
        mutationFn: ({ childId, name, phaseId }: { childId: number; name: string; phaseId: number }) =>
            addCustomChecklistItem(childId, { name, phaseId }),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['myChildren'] });
            const newItem = res.data;
            if (newItem && selectedRecChildId && newItem.phases?.some((p: ChecklistPhase) => p.id === selectedPhaseId)) {
                setChildren(prev =>
                    prev.map(c =>
                        c.id === selectedRecChildId
                            ? { ...c, checklistItems: [...c.checklistItems, newItem] }
                            : c
                    )
                );
            }
            setCustomItemName('');
            setShowCustomAdd(false);
        },
    });

    const deleteCustomMutation = useMutation({
        mutationFn: ({ childId, itemId }: { childId: number; itemId: number }) =>
            deleteChecklistItem(childId, itemId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['myChildren'] }),
    });

    const handlePhaseChange = (phaseId: number) => {
        if (phaseId <= 0 || !selectedRecChildId) return;
        setSelectedPhaseId(phaseId);
        setChecklistExpanded(false);
        writeMyRebabySelection(userId, selectedRecChildId, phaseId);
        fetchRecommendations(selectedRecChildId, phaseId, true);
    };

    const handleSelectChild = (childId: number) => {
        if (selectedRecChildId === childId) return;
        setSelectedRecChildId(childId);
        const child = children.find(c => c.id === childId);
        if (child) autoSelectPhaseForChild(child);
        else writeMyRebabySelection(userId, childId, selectedPhaseId);
    };

    const handleCheckChange = async (itemId: number, newChecked: boolean) => {
        if (!selectedRecChildId) return;
        setChecklistLoading(prev => ({ ...prev, [itemId]: true }));

        try {
            await updateChecklistMutation.mutateAsync({ childId: selectedRecChildId, itemId, isChecked: newChecked });
            fetchRecommendations(selectedRecChildId, selectedPhaseId, true);
            setChildren(prev =>
                prev.map(c =>
                    c.id === selectedRecChildId
                        ? {
                            ...c,
                            checklistItems: c.checklistItems.map(i =>
                                i.id === itemId ? { ...i, isChecked: newChecked } : i
                            ),
                        }
                        : c
                )
            );
        } catch (err) {
            console.error('Update failed', err);
        }
        setChecklistLoading(prev => ({ ...prev, [itemId]: false }));
    };

    const handleDeleteCustomItem = (itemId: number) => {
        if (!selectedRecChildId) return;
        setConfirmDeleteCustomId(itemId);
    };

    const confirmDeleteCustomItem = async () => {
        if (!selectedRecChildId || confirmDeleteCustomId == null) return;
        setConfirmBusy(true);
        try {
            await deleteCustomMutation.mutateAsync({
                childId: selectedRecChildId,
                itemId: confirmDeleteCustomId,
            });
            setChildren(prev =>
                prev.map(c =>
                    c.id === selectedRecChildId
                        ? {
                              ...c,
                              checklistItems: c.checklistItems.filter(
                                  i => i.id !== confirmDeleteCustomId
                              ),
                          }
                        : c
                )
            );
            setConfirmDeleteCustomId(null);
        } catch (err) {
            console.error('Error deleting custom item:', err);
        } finally {
            setConfirmBusy(false);
        }
    };

    const handleAddCustomItem = async () => {
        if (!customItemName.trim() || !selectedRecChildId || selectedPhaseId <= 0) return;
        setAddingCustom(true);
        try {
            await addCustomMutation.mutateAsync({
                childId: selectedRecChildId,
                name: customItemName,
                phaseId: selectedPhaseId,
            });
            fetchRecommendations(selectedRecChildId, selectedPhaseId, true);
        } catch (err) {
            console.error('Error adding custom item:', err);
        }
        setAddingCustom(false);
    };

    const openAddBaby = (expected?: boolean) => {
        setModalInitialExpected(expected ?? false);
        setSelectedChild(null);
        setIsModalOpen(true);
    };

    const handleEdit = (child: ChildDto) => {
        setModalInitialExpected(undefined);
        setSelectedChild(child);
        setIsModalOpen(true);
    };

    const syncChildrenQueryCache = (updater: (prev: ChildDto[]) => ChildDto[]) => {
        queryClient.setQueriesData<ChildDto[]>({ queryKey: ['myChildren'] }, (old) => {
            const prev = Array.isArray(old) ? old : [];
            return updater(prev);
        });
        void queryClient.invalidateQueries({ queryKey: ['myChildren'] });
    };

    const handleDelete = (id: number) => {
        setConfirmDeleteChildId(id);
    };

    const confirmDeleteChild = async () => {
        if (confirmDeleteChildId == null) return;
        const id = confirmDeleteChildId;
        setConfirmBusy(true);
        try {
            await deleteChild(id);
            setIsModalOpen(false);
            setChildren(prev => prev.filter(c => c.id !== id));
            syncChildrenQueryCache(prev => prev.filter(c => c.id !== id));
            if (selectedRecChildId === id) {
                setSelectedRecChildId(null);
                writeMyRebabySelection(userId, null, 0);
            }
            setConfirmDeleteChildId(null);
        } catch (err) {
            console.error('Error deleting child:', err);
        } finally {
            setConfirmBusy(false);
        }
    };

    const handleSave = (updatedChild: ChildDto) => {
        setIsModalOpen(false);
        const isEdit = !!selectedChild;

        setChildren(prev => {
            if (isEdit) {
                return prev.map(c => (c.id === updatedChild.id ? updatedChild : c));
            }
            if (prev.some(c => c.id === updatedChild.id)) {
                return prev.map(c => (c.id === updatedChild.id ? updatedChild : c));
            }
            return [...prev, updatedChild];
        });

        syncChildrenQueryCache(prev => {
            if (isEdit || prev.some(c => c.id === updatedChild.id)) {
                return prev.map(c => (c.id === updatedChild.id ? updatedChild : c));
            }
            return [...prev, updatedChild];
        });

        if (!isEdit) {
            setSelectedRecChildId(updatedChild.id);
            selectedRecChildIdRef.current = updatedChild.id;
            setGrowthInsight(null);
            setRecommendations(prev => ({ ...prev, [updatedChild.id]: [] }));
            autoSelectPhaseForChild(updatedChild, true);
        }
    };

    const handleFind = () => {
        if (selectedRecChildId && selectedPhaseId > 0) {
            void fetchRecommendations(selectedRecChildId, selectedPhaseId, true);
        }
        // Center results in free viewport so Find CTA + bottom nav stay visible (mobile).
        if (productsRef.current) {
            scrollToElement(productsRef.current, { behavior: 'smooth', block: 'center' });
        }
    };

    const selectedChildData = children.find(c => c.id === selectedRecChildId);
    const selectedRecs = selectedRecChildId ? recommendations[selectedRecChildId] || [] : [];
    const sortedRecs = useMemo(
        () => sortProductsClient(selectedRecs, recSortKey),
        [selectedRecs, recSortKey]
    );
    const selectedPhase = phases.find((p) => p.id === selectedPhaseId);
    const phaseAccent = useMemo(() => resolvePhaseAccent(selectedPhase), [selectedPhase]);

    const isLoading = childrenLoading || phases.length === 0;
    if (isLoading) return <LoadingSpinner />;

    const getChecklistSections = () => {
        if (!selectedChildData?.checklistItems) {
            return { relevant: [] as ChecklistItemWithPhase[] };
        }

        const relevant: ChecklistItemWithPhase[] = [];
        selectedChildData.checklistItems.forEach((item: ChecklistItemWithPhase) => {
            const itemPhases = item.phases || [];
            if (itemPhases.length === 0) {
                relevant.push(item);
                return;
            }
            if (itemPhases.some(p => p.id === selectedPhaseId)) relevant.push(item);
        });

        return { relevant };
    };

    const { relevant } = getChecklistSections();

    const currentPhase = (() => {
        if (!selectedChildData) return undefined;
        if (selectedChildData.expectedBirthDate && !selectedChildData.birthDate) {
            return phases.find((p) => p.minAgeMonths < 0);
        }
        if (!selectedChildData.birthDate) return undefined;
        const months = Math.floor(
            (Date.now() - new Date(selectedChildData.birthDate).getTime()) /
                (1000 * 60 * 60 * 24 * 30.4375)
        );
        return phases
            .filter((p) => p.minAgeMonths >= 0)
            .find((p) => months >= p.minAgeMonths && months <= p.maxAgeMonths);
    })();

    const getPhaseProgress = (phaseId: number) => {
        if (!selectedChildData?.checklistItems) return { done: 0, total: 0 };
        const items = selectedChildData.checklistItems.filter((item) =>
            item.phases?.some((p) => p.id === phaseId)
        );
        return {
            done: items.filter((i) => i.isChecked).length,
            total: items.length,
        };
    };

    const doneCounts = Object.fromEntries(phases.map((p) => [p.id, getPhaseProgress(p.id).done]));
    const totalCounts = Object.fromEntries(phases.map((p) => [p.id, getPhaseProgress(p.id).total]));

    const phaseProgress = getPhaseProgress(selectedPhaseId);
    const progressPct = phaseProgress.total > 0 ? Math.round((phaseProgress.done / phaseProgress.total) * 100) : 0;

    const insightPhase = phases.find((p) => p.id === growthInsight?.recommendedPhaseId);
    const insightPhaseLabel = formatInsightPhaseLabel(
        insightPhase,
        t,
        growthInsight?.recommendedPhaseName ?? ''
    );
    const insightMessage =
        growthInsight?.messageKey
            ? t(growthInsight.messageKey, {
                  name: selectedChildData?.name ?? '',
                  phase: insightPhaseLabel,
              })
            : growthInsight?.message ?? '';

    return (
        <div
            className="text-[var(--text-color)]"
            style={
                {
                    // Strong = CMS accent (Figma `color`); soft = low-opacity mix (Figma `bgColor`)
                    ['--phase-accent' as string]: phaseAccent,
                    ['--phase-accent-soft' as string]:
                        phaseAccent.startsWith('#') || phaseAccent.startsWith('rgb')
                            ? `color-mix(in srgb, ${phaseAccent} 16%, white)`
                            : `color-mix(in srgb, ${phaseAccent} 16%, white)`,
                } as React.CSSProperties
            }
        >
            <div className="bg-gradient-to-b from-[var(--bg-color)] to-[color-mix(in_srgb,var(--bg-color),white_20%)] pb-6 pt-4">
                <div className="container mx-auto max-w-7xl">
                    <BackToHomeLink />
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="mb-1 flex items-center gap-3">
                                <div
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                                    style={{ backgroundColor: 'var(--phase-accent-soft)' }}
                                >
                                    <Baby className="h-5 w-5 text-[var(--text-color)]" />
                                </div>
                                <div className="min-w-0">
                                    <h1 className="text-2xl font-bold sm:text-3xl">{tr('page.myrebaby.title')}</h1>
                                    <p className="text-sm opacity-75">
                                        {tr('page.myrebaby.subtitle')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            data-tour-id="mypetito.addbaby"
                            onClick={() => openAddBaby(false)}
                            className="min-h-11 w-full scroll-mt-[calc(var(--shop-header-height,7rem)+0.75rem)] rounded-full bg-[var(--accent-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 sm:w-auto"
                        >
                            + {tr('page.myrebaby.addbaby')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="container mx-auto max-w-7xl -mt-2 pb-10">

            {children.length > 0 && (
                <div className="mb-5 flex items-center gap-3">
                    <div className="scroll-x-fade min-w-0 flex-1 flex-nowrap overflow-x-auto pb-1">
                    {children.map((child) => {
                        const isSelected = child.id === selectedRecChildId;
                        return (
                            <button
                                key={child.id}
                                type="button"
                                onClick={() => handleSelectChild(child.id)}
                                className={
                                    isSelected
                                        ? 'inline-flex shrink-0 items-center gap-2 rounded-full border-2 px-4 py-2 min-h-11 shadow-sm'
                                        : 'shrink-0 rounded-full border border-[color-mix(in_srgb,var(--text-color),transparent_80%)] bg-[var(--bg-color)] px-3 py-1.5 min-h-11 text-sm hover:opacity-90'
                                }
                                style={
                                    isSelected
                                        ? {
                                              borderColor: 'var(--phase-accent)',
                                              backgroundColor: 'var(--phase-accent-soft)',
                                          }
                                        : undefined
                                }
                            >
                                {isSelected ? (
                                    <>
                                        <span
                                            className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-[var(--text-color)]"
                                            style={{ backgroundColor: 'var(--phase-accent-soft)' }}
                                        >
                                            {child.name.charAt(0).toUpperCase()}
                                        </span>
                                        <span className="font-semibold">{child.name}</span>
                                        <span className="text-sm opacity-60">{getChildAgeLabel(child)}</span>
                                        <span className="text-base" aria-hidden>{getChildAvatarEmoji(child)}</span>
                                    </>
                                ) : (
                                    child.name
                                )}
                            </button>
                        );
                    })}
                    </div>
                    {selectedChildData && (
                        <button
                            type="button"
                            onClick={() => handleEdit(selectedChildData)}
                            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center text-sm opacity-50 underline"
                            aria-label="Edit child"
                        >
                            <Edit2 className="inline h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            )}

            {selectedRecChildId && selectedChildData ? (
                <>
                    <div className="mb-6">
                        <GrowthRoadmap
                            phases={phases}
                            selectedPhaseId={selectedPhaseId}
                            currentPhaseId={currentPhase?.id}
                            doneCounts={doneCounts}
                            totalCounts={totalCounts}
                            onSelectPhase={handlePhaseChange}
                            t={t}
                            phaseAccent={phaseAccent}
                        />
                    </div>

                    {/* Growth insight — keep algorithmic slower/faster guidance */}
                    {isInsightLoading ? (
                        <div className="mb-5"><LoadingSpinner /></div>
                    ) : growthInsight?.hasRecommendation ? (
                        <div
                            className="mb-5 p-4 rounded-xl border flex items-start gap-3"
                            style={{
                                backgroundColor: 'var(--phase-accent-soft)',
                                borderColor: 'var(--phase-accent)',
                            }}
                        >
                            <span className="text-2xl">📈</span>
                            <div className="flex-1">
                                <p className="font-medium text-sm sm:text-base">{insightMessage}</p>
                                <button
                                    type="button"
                                    onClick={() => handlePhaseChange(growthInsight.recommendedPhaseId!)}
                                    className="mt-2 text-sm font-medium underline opacity-80 hover:opacity-100"
                                >
                                    {t('page.myrebaby.growth.recommend')} {insightPhaseLabel}
                                </button>
                            </div>
                        </div>
                    ) : null}

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Checklist */}
                        <section
                            className="rounded-2xl bg-[color-mix(in_srgb,var(--bg-color),white_40%)] shadow-sm border border-[color-mix(in_srgb,var(--text-color),transparent_90%)] overflow-hidden"
                        >
                            {selectedPhase ? (
                                <>
                                    <div
                                        className="flex items-center justify-between gap-3 px-5 py-4"
                                        style={{ backgroundColor: 'var(--phase-accent-soft)' }}
                                    >
                                        <div>
                                            <h2 className="font-bold text-[var(--text-color)]">
                                                {tr('page.myrebaby.whatyouneed')}
                                            </h2>
                                            <span className="text-xs opacity-70">
                                                {phaseProgress.done} {tr('page.myrebaby.of')} {phaseProgress.total}{' '}
                                                {tr('page.myrebaby.itemschecked')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="relative inline-flex h-11 w-11 items-center justify-center">
                                                <svg className="h-11 w-11 -rotate-90" viewBox="0 0 36 36">
                                                    <path
                                                        strokeWidth="3"
                                                        fill="none"
                                                        stroke="var(--phase-accent)"
                                                        opacity={0.2}
                                                        d="M18 2.5 a 15.5 15.5 0 1 1 0 31 a 15.5 15.5 0 1 1 0 -31"
                                                    />
                                                    <path
                                                        strokeWidth="3"
                                                        fill="none"
                                                        stroke="var(--phase-accent)"
                                                        strokeLinecap="round"
                                                        strokeDasharray={`${progressPct}, 100`}
                                                        d="M18 2.5 a 15.5 15.5 0 1 1 0 31 a 15.5 15.5 0 1 1 0 -31"
                                                    />
                                                </svg>
                                                <span className="absolute text-xs font-bold text-[var(--text-color)]">{progressPct}%</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setShowCustomAdd(v => !v)}
                                                className="rounded-full px-3 py-1.5 text-xs font-semibold border hover:opacity-90"
                                                style={{
                                                    borderColor: 'var(--phase-accent)',
                                                    backgroundColor: 'var(--phase-accent-soft)',
                                                    color: 'var(--text-color)',
                                                }}
                                            >
                                                + {tr('page.myrebaby.add')}
                                            </button>
                                        </div>
                                    </div>

                                    <PhaseEducationalCards
                                        content={selectedPhase?.content}
                                        t={t}
                                        phaseAccent={phaseAccent}
                                        className="m-4 lg:hidden"
                                    />

                                    <ul
                                        data-tour-id="mypetito.checklist"
                                        className="max-h-[28rem] scroll-mt-[calc(var(--shop-header-height,7rem)+0.75rem)] overflow-y-auto divide-y divide-[color-mix(in_srgb,var(--text-color),transparent_92%)]"
                                    >
                                        {(checklistExpanded || relevant.length <= CHECKLIST_PREVIEW_COUNT
                                            ? relevant
                                            : relevant.slice(0, CHECKLIST_PREVIEW_COUNT)
                                        ).map(item => (
                                            <ChecklistItemRow
                                                key={item.id}
                                                item={item}
                                                childId={selectedRecChildId}
                                                checklistLoading={checklistLoading}
                                                onCheckChange={handleCheckChange}
                                                onDelete={handleDeleteCustomItem}
                                            />
                                        ))}
                                        {relevant.length === 0 && (
                                            <li className="px-5 py-8 text-center text-sm opacity-50">
                                                {tr('page.myrebaby.nochecklist')}
                                            </li>
                                        )}
                                    </ul>
                                    {relevant.length > CHECKLIST_PREVIEW_COUNT && (
                                        <div className="border-t border-[color-mix(in_srgb,var(--text-color),transparent_92%)] px-5 py-3">
                                            <button
                                                type="button"
                                                onClick={() => setChecklistExpanded((v) => !v)}
                                                className="w-full min-h-11 rounded-xl text-sm font-semibold text-[var(--text-color)] underline-offset-2 hover:underline"
                                            >
                                                {checklistExpanded
                                                    ? tr('page.myrebaby.checklist.showless')
                                                    : tr('page.myrebaby.checklist.showall').replace(
                                                          '{count}',
                                                          String(relevant.length)
                                                      )}
                                            </button>
                                        </div>
                                    )}

                                    {showCustomAdd && (
                                        <div className="flex flex-col gap-2 border-t border-[color-mix(in_srgb,var(--text-color),transparent_90%)] p-4 sm:flex-row">
                                            <input
                                                type="text"
                                                value={customItemName}
                                                onChange={e => setCustomItemName(e.target.value)}
                                                placeholder={tr('page.myrebaby.customitem.placeholder')}
                                                className="flex-1 rounded-xl border border-[color-mix(in_srgb,var(--text-color),transparent_80%)] bg-[var(--bg-color)] px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--phase-accent)]"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddCustomItem}
                                                disabled={addingCustom || !customItemName.trim()}
                                                className="rounded-xl px-4 py-2 text-sm font-medium text-[var(--text-color)] disabled:opacity-50"
                                                style={{ backgroundColor: 'var(--phase-accent-soft)', border: '1px solid var(--phase-accent)' }}
                                            >
                                                {addingCustom ? t('state.saving') : tr('page.myrebaby.customitem.add')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex min-h-[200px] flex-col items-center justify-center p-8 text-center">
                                    <p className="font-semibold">{tr('page.myrebaby.tapmilestone')}</p>
                                </div>
                            )}
                        </section>

                        <div className="hidden lg:block">
                            <PhaseEducationalCards
                                content={selectedPhase?.content}
                                t={t}
                                phaseAccent={phaseAccent}
                            />
                        </div>
                    </div>

                    {/* Centered Find — between checklist block and products */}
                    <div className="my-8 flex justify-center">
                        <button
                            type="button"
                            data-tour-id="mypetito.find"
                            onClick={handleFind}
                            className="inline-flex scroll-mt-[calc(var(--shop-header-height,7rem)+0.75rem)] items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-[var(--text-color)] shadow-sm hover:opacity-90"
                            style={{
                                backgroundColor: 'var(--phase-accent-soft)',
                                border: '1.5px solid var(--phase-accent)',
                            }}
                        >
                            <Search className="h-4 w-4" />
                            {tr('page.myrebaby.find')}
                        </button>
                    </div>

                    {/* Horizontal product recommendations (Figma yellow region) */}
                    <div
                        ref={productsRef}
                        data-tour-id="mypetito.results"
                        className="scroll-mt-[calc(var(--shop-header-height,7rem)+0.75rem)]"
                    >
                        <h3 className="mb-3 text-sm font-semibold text-[var(--text-color)]">
                            {t('myrebaby.recommendations.products')}
                        </h3>
                        {recLoading[selectedRecChildId!] ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                <ProductResultsToolbar
                                    count={sortedRecs.length}
                                    sort={recSortKey}
                                    onSortChange={setRecSortKey}
                                    showCount={true}
                                />
                                {sortedRecs.length > 0 ? (
                                    <div className="flex gap-4 overflow-x-auto pb-4 snap-x scrollbar-thin">
                                        {sortedRecs.map(product => (
                                            <div key={product.id} className="w-[min(100%,280px)] flex-shrink-0 snap-start">
                                                <ProductCard
                                                    product={product}
                                                    showPrice={!!isAuthReady}
                                                    hideOwnBadge={true}
                                                    childId={selectedRecChildId}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm opacity-50 py-4">
                                        {tr('page.myrebaby.norecs')}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </>
            ) : (
                <section
                    data-tour-id="mypetito.empty"
                    className="rounded-2xl bg-[color-mix(in_srgb,var(--bg-color),white_15%)] p-8 sm:p-12 text-center shadow-md"
                >
                    <div className="text-5xl mb-4">👶</div>
                    <h2 className="text-xl font-bold mb-2">{t('myrebaby.recommendations')}</h2>
                    <p className="opacity-70 mb-6 max-w-md mx-auto">{t('myrebaby.recommendations.select')}</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <button
                            type="button"
                            data-tour-id="mypetito.addbaby.empty"
                            onClick={() => openAddBaby(false)}
                            className="inline-flex items-center gap-2 bg-[var(--accent-color)] text-white px-5 py-2.5 rounded-xl font-medium hover:opacity-90 transition-opacity"
                        >
                            <Plus className="h-4 w-4" />
                            {t('form.childmodal.title.add')}
                        </button>
                        <button
                            type="button"
                            onClick={() => openAddBaby(true)}
                            className="inline-flex items-center gap-2 border-2 border-[var(--accent-color)] px-5 py-2.5 rounded-xl font-medium hover:bg-[var(--accent-color)]/30 transition-colors"
                        >
                            🤰 {t('form.childmodal.title.expected')}
                        </button>
                    </div>
                </section>
            )}

            <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} className="relative z-50">
                <div className="fixed inset-0 bg-[color-mix(in_srgb,var(--text-color),transparent_80%)] backdrop-blur-sm" aria-hidden="true" />
                {/* Centered on all viewports (mobile was items-end / bottom-sheet before). */}
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="max-h-[min(92dvh,40rem)] w-full max-w-md overflow-y-auto rounded-2xl bg-[var(--bg-color)] p-5 shadow-2xl transform transition-all duration-300 ease-in-out sm:p-6 lg:p-8">
                        <ChildModal
                            selectedChild={selectedChild}
                            initialIsExpected={modalInitialExpected}
                            onSave={handleSave}
                            onClose={() => setIsModalOpen(false)}
                            onDelete={handleDelete}
                        />
                    </Dialog.Panel>
                </div>
            </Dialog>

            <ConfirmModal
                open={confirmDeleteCustomId != null}
                title={tr('myrebaby.confirm.deleteitem.title') || 'Delete checklist item'}
                message={tr('myrebaby.confirm.deleteitem.message') || 'Delete this custom item?'}
                confirmLabel={tr('common.delete') || 'Delete'}
                danger
                busy={confirmBusy}
                onConfirm={() => void confirmDeleteCustomItem()}
                onCancel={() => !confirmBusy && setConfirmDeleteCustomId(null)}
            />
            <ConfirmModal
                open={confirmDeleteChildId != null}
                title={tr('myrebaby.confirm.deletechild.title') || 'Remove child'}
                message={tr('myrebaby.confirm.deletechild.message') || 'Are you sure you want to remove this child from your journey?'}
                confirmLabel={tr('common.delete') || 'Delete'}
                danger
                busy={confirmBusy}
                onConfirm={() => void confirmDeleteChild()}
                onCancel={() => !confirmBusy && setConfirmDeleteChildId(null)}
            />
            </div>
        </div>
    );
};

export default MyRebaby;
